import React, { createContext, useCallback, useContext, useEffect, useReducer } from "react";

const STORAGE_KEY = "md-game-state-v1";

const initialState = {
  totalScore: 0,
  bestScore: 0,
  achievements: [],   // string[]
  unlockedPhotos: 0,  // count of photos unlocked across levels
  levelsCleared: [],  // ['catch','puzzle','match','boss']
  popup: null,        // { kind, title, sub, ts }
};

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const saved = JSON.parse(raw);
    return { ...initialState, ...saved, popup: null };
  } catch {
    return initialState;
  }
};

const persist = (s) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      bestScore: s.bestScore,
      achievements: s.achievements,
    }));
  } catch {}
};

const reducer = (state, action) => {
  switch (action.type) {
    case "ADD_SCORE": {
      const totalScore = state.totalScore + action.points;
      const bestScore = Math.max(state.bestScore, totalScore);
      return { ...state, totalScore, bestScore };
    }
    case "CLEAR_LEVEL": {
      if (state.levelsCleared.includes(action.level)) return state;
      return {
        ...state,
        levelsCleared: [...state.levelsCleared, action.level],
        unlockedPhotos: state.unlockedPhotos + (action.photosUnlocked || 0),
      };
    }
    case "ADD_ACHIEVEMENT": {
      if (state.achievements.includes(action.id)) return state;
      const next = {
        ...state,
        achievements: [...state.achievements, action.id],
        popup: { kind: "achievement", title: action.title, sub: action.sub, ts: Date.now() },
      };
      return next;
    }
    case "POPUP":
      return { ...state, popup: { ...action.popup, ts: Date.now() } };
    case "CLEAR_POPUP":
      return { ...state, popup: null };
    case "RESET":
      return { ...load(), totalScore: 0, levelsCleared: [], unlockedPhotos: 0 };
    case "RESET_ALL":
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      return initialState;
    default:
      return state;
  }
};

const GameCtx = createContext(null);

export const GameProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, undefined, load);

  useEffect(() => { persist(state); }, [state.bestScore, state.achievements, state]);

  const addScore = useCallback((points) => dispatch({ type: "ADD_SCORE", points }), []);
  const clearLevel = useCallback((level, photosUnlocked = 1) => dispatch({ type: "CLEAR_LEVEL", level, photosUnlocked }), []);
  const addAchievement = useCallback((id, title, sub) => dispatch({ type: "ADD_ACHIEVEMENT", id, title, sub }), []);
  const showPopup = useCallback((popup) => dispatch({ type: "POPUP", popup }), []);
  const clearPopup = useCallback(() => dispatch({ type: "CLEAR_POPUP" }), []);
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);
  const resetAll = useCallback(() => dispatch({ type: "RESET_ALL" }), []);

  const value = { state, addScore, clearLevel, addAchievement, showPopup, clearPopup, reset, resetAll };
  return <GameCtx.Provider value={value}>{children}</GameCtx.Provider>;
};

export const useGame = () => {
  const ctx = useContext(GameCtx);
  if (!ctx) throw new Error("useGame must be inside GameProvider");
  return ctx;
};
